import { api } from './index';

const BASE_PATH = '/api/internal/route_plans';

export type RouteAssignment = {
  id: number;
  routePlanVisitId: number;
  caregiverId: number;
  caregiverFullName: string;
  patientId: number;
  patientFullName: string;
  patientAddress: string;
  patientAddressLabel: string | null;
  patientLatitude: number | null;
  patientLongitude: number | null;
  arrivalTime: string;
  humanArrivalTime: string;
  departureTime: string;
  humanDepartureTime: string;
  travelTime: number | null;
  humanTravelTime: string | null;
  serviceTime: number;
  sequence: number;
  status: 'accepted' | 'rejected' | 'applied';
  shiftId: number | null;
  favoriteFallback: boolean;
  manual: boolean;
};

/** A visit skipped at apply time because the patient already has an overlapping shift. */
export type SkippedDuplicate = {
  routePlanVisitId: number;
  patientId: number;
  patientName: string;
  arrivalTime: string;
  conflictingShiftId: number;
};

/** One apply-readiness finding: a blocker (prevents applying) or a warning (informative). */
export type ApplyFinding = {
  type: 'no_accepted_assignments' | 'overlapping_assignment' | 'stale_reschedule'
    | 'stale_manual_move' | 'out_of_scope_shift' | 'unplanned_shift' | 'schedule_block';
  message: string;
  shiftId: number | null;
  patientName: string | null;
  caregiverName: string | null;
  startTime: string | null;
  endTime: string | null;
};

/** Dry run of the apply gate: what would block, warn or be skipped if applied right now. */
export type ApplyPreview = {
  blockers: ApplyFinding[];
  warnings: ApplyFinding[];
  skippedDuplicates: SkippedDuplicate[];
  creatableCount: number;
};

/** Result of a hand-place / drag-move: the refreshed plan plus soft (overridable) warnings. */
export type PlacementResult = {
  routePlan: RoutePlan;
  warnings: string[];
};

/** Where to pin a visit; overrideBlocked confirms assigning a blocked professional. */
export type PlacementAttrs = {
  caregiverId: number;
  arrivalTime: string;
  overrideBlocked?: boolean;
};

function placementData({ caregiverId, arrivalTime, overrideBlocked }: PlacementAttrs) {
  return { caregiverId, arrivalTime, ...(overrideBlocked ? { overrideBlocked } : {}) };
}

export type RoutePlanVisit = {
  id: number;
  patientId: number;
  patientFullName: string;
  patientAddress: string;
  patientAddressId: number | null;
  patientAddressLabel: string | null;
  durationMinutes: number;
  timeWindowStart: string | null;
  timeWindowEnd: string | null;
  fixedStartTime: string | null;
  assignedCaregiverId: number | null;
  assignedCaregiverFullName: string | null;
  skillId: number | null;
  skillName: string | null;
  blockedCaregiverIds: number[];
  patientLatitude: number | null;
  patientLongitude: number | null;
  prioritized: boolean;
  serviceCategoryId: number | null;
  serviceCategoryName: string | null;
  serviceCategoryPriority: number | null;
  serviceCategoryPrioritizationEnabled: boolean | null;
};

// Editable visit attributes (PUT route_plan_visits/:id). Time fields accept null so switching
// between fixed-start and time-window modes clears the unused one.
export type RoutePlanVisitUpdate = {
  durationMinutes?: number;
  timeWindowStart?: string | null;
  timeWindowEnd?: string | null;
  fixedStartTime?: string | null;
  skillId?: number | null;
  patientAddressId?: number | null;
};

export type RoutePlanCaregiver = {
  id: number;
  caregiverId: number;
  caregiverFullName: string;
  caregiverCareer: string;
  caregiverAddress: string;
  hasCoordinates: boolean;
  caregiverLatitude: number | null;
  caregiverLongitude: number | null;
  availableFrom: string;
  availableTo: string;
  lunchDurationMinutes: number;
};

/**
 * Enriched existing shift (new route plans with shift_id_map). A shift outside the viewer's
 * scope (a shared caregiver's patient in another group) comes anonymous: patient fields null,
 * never movable.
 */
export type EnrichedExistingShift = {
  shiftId: number;
  patientId: number | null;
  startTime: string;
  endTime: string;
  humanStartTime: string;
  humanEndTime: string;
  /** Original shift times; differ from human* when the optimizer slid the shift. */
  originalStartTime: string;
  originalEndTime: string;
  rescheduled: boolean;
  /** True when VROOM couldn't fold the shift into the route; shown at its real time. */
  dropped: boolean;
  travelTime: number;
  humanTravelTime: string | null;
  patientFullName: string | null;
  patientAddress: string | null;
  patientLatitude: number | null;
  patientLongitude: number | null;
  /** The board may move/reassign this shift by hand (future, unattended, not in a 24h chain). */
  movable: boolean;
  /** A manual board move is proposed for this shift (persisted on apply). */
  moved: boolean;
  /** The solve never planned around this shift (created after optimizing, or no solve). */
  unplanned: boolean;
  /** Set only when the manual move reassigns the shift to another caregiver. */
  originalCaregiverName?: string | null;
};

/** Basic time block (old route plans without shift_id_map). Not exported — use isEnrichedShift() to narrow. */
type BasicExistingShift = {
  startTime: string;
  endTime: string;
};

export type ExistingShiftStep = EnrichedExistingShift | BasicExistingShift;

export function isEnrichedShift(step: ExistingShiftStep): step is EnrichedExistingShift {
  return 'shiftId' in step;
}

export type MissingExpectedVisit = {
  patientId: number;
  patientFullName: string;
  fixedStartTime: string | null;
  timeWindowStart: string | null;
  timeWindowEnd: string | null;
};

// 'blocked': uncovered and the patient's schedule block leaves no room for it on that date —
// still expected (contractual), never quick-addable.
export type CoverageSlotState = 'assigned' | 'existing_shift' | 'pending' | 'missing' | 'blocked';

export type CoverageScheduleBlock = { id: number; description: string };

export type CoverageRequirement = {
  fixedStartTime: string | null;
  timeWindowStart: string | null;
  timeWindowEnd: string | null;
  durationMinutes: number;
  skillId: number | null;
  skillName: string | null;
  preferredCaregiverId: number | null;
  preferredCaregiverName: string | null;
};

export type CoverageSlotCoverage = {
  kind: 'assignment' | 'shift' | 'visit';
  assignmentId: number | null;
  routePlanVisitId: number | null;
  shiftId: number | null;
  caregiverId: number | null;
  caregiverName: string | null;
  startTime: string | null;
  endTime: string | null;
  assignmentStatus: 'accepted' | 'rejected' | 'applied' | null;
};

export type CoverageSlot = {
  weeklyPlanId: number;
  patientId: number;
  patientFullName: string;
  requirement: CoverageRequirement;
  state: CoverageSlotState;
  coverage: CoverageSlotCoverage | null;
  timeDrift: boolean;
  caregiverDrift: boolean;
  scheduleBlock: CoverageScheduleBlock | null;
  serviceCategoryId: number | null;
  serviceCategoryName: string | null;
  serviceCategoryPriority: number | null;
};

export type CoverageExtraVisit = {
  routePlanVisitId: number | null;
  patientId: number;
  patientFullName: string;
  state: 'assigned' | 'pending';
  caregiverName: string | null;
  startTime: string | null;
  fixedStartTime: string | null;
  timeWindowStart: string | null;
  timeWindowEnd: string | null;
  durationMinutes: number | null;
  serviceCategoryId: number | null;
  serviceCategoryName: string | null;
  serviceCategoryPriority: number | null;
};

export type CoverageDoubleEntry = {
  assignmentId: number;
  routePlanVisitId: number;
  patientId: number;
  patientFullName: string;
  assignmentCaregiverName: string | null;
  arrivalTime: string | null;
  /** Null (with the caregiver name) when the shift is outside the viewer's scope. */
  shiftId: number | null;
  shiftCaregiverName: string | null;
  shiftStartTime: string | null;
  shiftEndTime: string | null;
};

export type CoverageSummary = {
  expected: number;
  assigned: number;
  existingShifts: number;
  pending: number;
  missing: number;
  blocked: number;
  extra: number;
  doubleCoverage: number;
};

export type RoutePlanCoverage = {
  carePlanningEnabled: boolean;
  date: string;
  career: string;
  groupId: number | null;
  slots: CoverageSlot[];
  extraVisits: CoverageExtraVisit[];
  doubleCoverage: CoverageDoubleEntry[];
  summary: CoverageSummary;
};

export type UnassignedTier = 'input_error' | 'capacity';

export type UnassignedVisit = {
  routePlanVisitId: number;
  patientName: string;
  tier: UnassignedTier;
  reasonCode: string;
  reasonText: string;
  blockingCaregiverId: number | null;
  blockingCaregiverName: string | null;
  constraints: string;
};

export type RoutePlan = {
  id: number;
  date: string;
  status: 'draft' | 'processing' | 'completed' | 'failed' | 'applied';
  career: string;
  humanCareer: string;
  humanDate: string;
  groupId: number | null;
  groupName: string | null;
  optimizerFixedStartOffset: number | null;
  totalTravelTime: number | null;
  humanTotalTravelTime: string | null;
  totalServiceTime: number | null;
  humanTotalServiceTime: string | null;
  caregiverCount: number;
  assignedCount: number;
  unassignedCount: number | null;
  unassignedVisits: UnassignedVisit[];
  errorMessage: string | null;
  createdAt: string;
  humanCreatedAt: string;
  routePlanVisits: RoutePlanVisit[];
  routePlanCaregivers: RoutePlanCaregiver[];
  routeAssignments: RouteAssignment[];
  caregiverBreaks: Record<number, { startTime: string; endTime: string }>;
  caregiverExistingShifts: Record<number, ExistingShiftStep[]>;
  missingExpectedVisits: MissingExpectedVisit[];
};

export interface RoutePlanForm {
  date: string;
  career: string;
  groupId?: number | null;
  optimizerFixedStartOffset?: number | null;
  routePlanVisitsAttributes: Array<{
    id?: number;
    patientId: number;
    patientAddressId?: number | null;
    durationMinutes: number;
    timeWindowStart?: string;
    timeWindowEnd?: string;
    fixedStartTime?: string;
    assignedCaregiverId?: number | null;
    skillId?: number | null;
    prioritized?: boolean;
    _destroy?: boolean;
  }>;
  routePlanCaregiversAttributes: Array<{
    id?: number;
    caregiverId: number;
    availableFrom?: string;
    availableTo?: string;
    lunchDurationMinutes?: number;
    _destroy?: boolean;
  }>;
}

export const routePlansApi = {
  index(groupIds?: number[]): Promise<RoutePlan[]> {
    const params: Record<string, unknown> = {};
    if (groupIds && groupIds.length > 0) params['group_id_in[]'] = groupIds;

    return api({
      method: 'get',
      url: BASE_PATH,
      params,
    }).then(response => response.data.routePlans);
  },

  show(id: number): Promise<RoutePlan> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}`,
    }).then(response => response.data.routePlan);
  },

  create(routePlanForm: RoutePlanForm): Promise<RoutePlan> {
    return api({
      method: 'post',
      url: BASE_PATH,
      data: { routePlan: routePlanForm },
    }).then(response => response.data.routePlan);
  },

  update(id: number, routePlanForm: Partial<RoutePlanForm>): Promise<RoutePlan> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}`,
      data: { routePlan: routePlanForm },
    }).then(response => response.data.routePlan);
  },

  optimize(id: number): Promise<RoutePlan> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/optimize`,
    }).then(response => response.data.routePlan);
  },

  updateVisit(
    id: number, visitId: number, attrs: RoutePlanVisitUpdate,
  ): Promise<RoutePlan> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}/route_plan_visits/${visitId}`,
      data: { routePlanVisit: attrs },
    }).then(response => response.data.routePlan);
  },

  updateCaregiver(
    id: number, routePlanCaregiverId: number,
    attrs: {
      availableFrom?: string;
      availableTo?: string;
      lunchStart?: string;
      lunchDurationMinutes?: number;
    },
  ): Promise<PlacementResult> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}/route_plan_caregivers/${routePlanCaregiverId}`,
      data: { routePlanCaregiver: attrs },
    }).then(response => ({
      routePlan: response.data.routePlan,
      warnings: response.data.meta?.warnings ?? [],
    }));
  },

  addCaregiver(
    id: number, caregiverId: number,
    availability?: { availableFrom?: string; availableTo?: string },
  ): Promise<RoutePlan> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/route_plan_caregivers`,
      data: { routePlanCaregiver: { caregiverId, ...availability } },
    }).then(response => response.data.routePlan);
  },

  removeCaregiver(id: number, routePlanCaregiverId: number): Promise<RoutePlan> {
    return api({
      method: 'delete',
      url: `${BASE_PATH}/${id}/route_plan_caregivers/${routePlanCaregiverId}`,
    }).then(response => response.data.routePlan);
  },

  createVisit(
    id: number, attrs: Partial<RoutePlanForm['routePlanVisitsAttributes'][number]>,
  ): Promise<{ routePlan: RoutePlan; createdVisitId: number }> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/route_plan_visits`,
      data: { routePlanVisit: attrs },
    }).then(response => ({
      routePlan: response.data.routePlan,
      // Server-identified: inferring "the newest row" client-side is racy when other
      // coordinators create visits concurrently.
      createdVisitId: response.data.meta.createdVisitId,
    }));
  },

  destroyVisit(id: number, visitId: number): Promise<RoutePlan> {
    return api({
      method: 'delete',
      url: `${BASE_PATH}/${id}/route_plan_visits/${visitId}`,
    }).then(response => response.data.routePlan);
  },

  /** Propose moving/reassigning an EXISTING shift (persisted on apply; upserts per shift). */
  moveExistingShift(
    id: number, shiftId: number, caregiverId: number, startDatetime: string,
  ): Promise<PlacementResult> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/route_plan_shift_moves`,
      data: { shiftId, caregiverId, startDatetime },
    }).then(response => ({
      routePlan: response.data.routePlan,
      warnings: response.data.meta?.warnings ?? [],
    }));
  },

  /** Revert a proposed existing-shift move (keyed by shift id). */
  revertShiftMove(id: number, shiftId: number): Promise<RoutePlan> {
    return api({
      method: 'delete',
      url: `${BASE_PATH}/${id}/route_plan_shift_moves/${shiftId}`,
    }).then(response => response.data.routePlan);
  },

  /** Hand-place a pending visit onto a caregiver at an exact time (board drop). A blocked
   * professional rejects with 422 `blocked_professional` unless overrideBlocked is passed. */
  placeAssignment(
    id: number, visitId: number, placement: PlacementAttrs,
  ): Promise<PlacementResult> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/route_assignments`,
      data: { visitId, ...placementData(placement) },
    }).then(response => ({
      routePlan: response.data.routePlan,
      warnings: response.data.meta?.warnings ?? [],
    }));
  },

  /** Move a placed visit to another caregiver/time (re-runs the feasibility checks). Same
   * blocked-professional contract as placeAssignment. */
  moveAssignment(
    id: number, assignmentId: number, placement: PlacementAttrs,
  ): Promise<PlacementResult> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${id}/route_assignments/${assignmentId}`,
      data: placementData(placement),
    }).then(response => ({
      routePlan: response.data.routePlan,
      warnings: response.data.meta?.warnings ?? [],
    }));
  },

  /** Unplace: remove a placement, returning the visit to the pending pool (keeps the visit). */
  unplaceAssignment(id: number, assignmentId: number): Promise<RoutePlan> {
    return api({
      method: 'delete',
      url: `${BASE_PATH}/${id}/route_assignments/${assignmentId}`,
    }).then(response => response.data.routePlan);
  },

  /** Day-coverage reconciliation: expected slots vs existing shifts vs assignments/visits. */
  coverage(id: number): Promise<RoutePlanCoverage> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}/coverage`,
    }).then(response => response.data);
  },

  applyPreview(id: number): Promise<ApplyPreview> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}/apply_preview`,
    }).then(response => response.data);
  },

  reset(id: number): Promise<RoutePlan> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/reset`,
    }).then(response => response.data.routePlan);
  },

  /**
   * A blocked apply rejects with 422 `{ message: 'apply_blocked', errors, blockers, warnings }`
   * (camelized by the axios transform) and leaves the plan completed for the user to act on.
   */
  apply(id: number): Promise<SkippedDuplicate[]> {
    return api({
      method: 'post',
      url: `${BASE_PATH}/${id}/apply`,
    }).then(response => response.data.meta?.skippedDuplicates ?? []);
  },

  updateAssignment(
    routePlanId: number, assignmentId: number, status: 'accepted' | 'rejected',
  ): Promise<RouteAssignment> {
    return api({
      method: 'put',
      url: `${BASE_PATH}/${routePlanId}/route_assignments/${assignmentId}`,
      data: { status },
    }).then(response => response.data.routeAssignment);
  },

};
